import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target, Zap, Bell, BarChart3, CheckCircle, ChevronRight,
  ArrowRight, Shield, TrendingUp, Award, Filter, Search,
  ChevronDown, Lock, Users, Clock, Star
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/* ─── Data (sin cambios) ─── */
const LIVE_PROCESSES = [
  { id: "SECO-2025-001", entity: "INVIAS", dept: "Cundinamarca", value: "$ 485.000.000", score: 96, priority: "ALTA", tag: "Infraestructura" },
  { id: "SECO-2025-002", entity: "Min. Educación", dept: "Antioquia", value: "$ 230.000.000", score: 88, priority: "ALTA", tag: "Tecnología" },
  { id: "SECO-2025-003", entity: "ICBF", dept: "Bogotá D.C.", value: "$ 127.500.000", score: 74, priority: "REVISAR", tag: "Servicios" },
  { id: "SECO-2025-004", entity: "Alcaldía Medellín", dept: "Antioquia", value: "$ 312.000.000", score: 91, priority: "ALTA", tag: "Obras" },
];

const FEATURES = [
  { icon: Target, badge: "Tiempo Real", title: "Radar de Procesos SECOP II", desc: "Monitoreo continuo de miles de contrataciones en los 32 departamentos de Colombia. Actualización diaria automática.", stat: "+15.000 procesos" },
  { icon: Zap, badge: "IA Avanzada", title: "Análisis con Gemini 3 Flash", desc: "Lectura automática de pliegos y términos de referencia. Clasifica en Alta Prioridad, Revisar o Descartar.", stat: "98.4% precisión" },
  { icon: BarChart3, badge: "Algoritmo Propio", title: "Scoring Inteligente 0-100", desc: "Algoritmo de idoneidad según palabras clave de tu empresa, capacidad financiera y ubicación geográfica.", stat: "Score en segundos" },
  { icon: Bell, badge: "Notificaciones", title: "Alertas Automáticas", desc: "Recibe avisos antes de que cierre la presentación de ofertas. No pierdas ninguna oportunidad por tiempo.", stat: "Alertas instantáneas" },
];

const STEPS = [
  { num: "01", title: "Configura tu perfil", desc: "Agrega las palabras clave de tu empresa, sectores, departamentos y montos objetivo en menos de 2 minutos." },
  { num: "02", title: "IA analiza y prioriza", desc: "Gemini 3 Flash asigna un Score 0-100 a cada proceso del SECOP II según tu perfil empresarial." },
  { num: "03", title: "Postúlate a tiempo", desc: "Recibe alertas de los procesos que más te convienen y aplica antes del cierre con toda la información lista." },
];

const FAQS = [
  { q: "¿Necesito tarjeta de crédito para iniciar la prueba gratis?", a: "No, puedes registrarte en 30 segundos sin ingresar ninguna tarjeta. Tendrás acceso completo durante 7 días." },
  { q: "¿De dónde obtiene RadarLit la información?", a: "Nos conectamos directamente con los datos abiertos del SECOP II y Colombia Compra Eficiente, actualizando los procesos varias veces al día." },
  { q: "¿Puedo cancelar en cualquier momento?", a: "Sí, sin cláusulas de permanencia ni penalizaciones. Cancela con un clic desde tu panel de usuario." },
  { q: "¿Qué pasa después de los 7 días de prueba?", a: "Puedes elegir el Plan Básico ($17.000 COP/mes) o el Premium ($29.700 COP/mes). Si decides no continuar, no se te cobrará nada." },
];

/* ─── Animation variants ─── */
const FV = {
  hidden: { opacity: 0, y: 26, filter: "blur(4px)" },
  show:   { opacity: 1, y: 0,  filter: "blur(0px)", transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const SV = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.08 } },
};
const IV = {
  hidden: { opacity: 0, y: 20, filter: "blur(2px)" },
  show:   { opacity: 1, y: 0,  filter: "blur(0px)", transition: { duration: 0.5, ease: "easeOut" } },
};
const VP = { once: true, margin: "-80px" };

/* ─── PriorityBadge (lógica sin cambios) ─── */
function PriorityBadge({ priority }) {
  if (priority === "ALTA") return (
    <span
      className="px-2 py-0.5 rounded-full text-[10px] font-bold border"
      style={{
        background: "rgba(16,185,129,0.14)",
        color: "#6EE7B7",
        borderColor: "rgba(16,185,129,0.38)",
        textShadow: "0 0 8px rgba(16,185,129,0.5)",
      }}
    >
      ALTA PRIORIDAD
    </span>
  );
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
      REVISAR
    </span>
  );
}

/* ─── FaqItem (lógica sin cambios, animación mejorada) ─── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid="faq-item-accordion"
      className="rounded-2xl overflow-hidden mb-3"
      style={{
        background: "white",
        border: "1px solid #E2E8F0",
        boxShadow: open ? "0 4px 24px rgba(16,185,129,0.08)" : "0 1px 4px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.3s ease",
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left transition-colors"
        style={{ background: open ? "#F8FAFC" : "white" }}
      >
        <span className="font-semibold text-slate-800 text-sm sm:text-base pr-4 leading-snug">{q}</span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="shrink-0"
        >
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="faq-answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{ overflow: "hidden" }}
          >
            <div className="px-6 py-4 border-t border-slate-100" style={{ background: "#F8FAFC" }}>
              <p className="text-slate-600 text-sm leading-relaxed">{a}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Componente principal (lógica sin cambios) ─── */
export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ticker, setTicker] = useState(0);
  const heroRef = useRef(null);
  const [cursorPos, setCursorPos] = useState({ x: -2000, y: -2000 });

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setInterval(() => setTicker(n => (n + 1) % LIVE_PROCESSES.length), 3000);
    return () => clearInterval(t);
  }, []);

  const handleHeroMouseMove = (e) => {
    if (heroRef.current && window.innerWidth >= 1024) {
      const rect = heroRef.current.getBoundingClientRect();
      setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  if (user) return null;

  return (
    <div className="min-h-screen text-slate-100 overflow-x-hidden">

      {/* ════════════════════════════
          NAVBAR
      ════════════════════════════ */}
      <nav
        className="sticky top-0 z-50 backdrop-blur-xl"
        style={{
          background: "rgba(2,6,23,0.90)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "0 1px 40px rgba(0,0,0,0.5)",
        }}
      >
        <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center justify-between">
          {/* Logo */}
          <div data-testid="nav-brand-logo" className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                background: "rgba(16,185,129,0.12)",
                border: "1px solid rgba(16,185,129,0.35)",
                boxShadow: "0 0 16px rgba(16,185,129,0.2)",
              }}
            >
              <Target className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-bold text-white tracking-tight">RadarLit</span>
            <div
              className="hidden sm:flex items-center gap-1.5 rounded-full px-2.5 py-0.5 ml-1"
              style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)" }}
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
              <span className="text-emerald-400 text-[10px] font-semibold tracking-wide">SECOP II EN VIVO</span>
            </div>
          </div>

          {/* Nav links */}
          <div className="hidden md:flex items-center gap-6 text-sm">
            <a href="#como-funciona" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">Cómo funciona</a>
            <a href="#caracteristicas" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">Características</a>
            <Link data-testid="nav-link-precios" to="/precios" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">Precios</Link>
          </div>

          {/* CTAs */}
          <div className="flex items-center gap-2">
            <Link
              data-testid="nav-link-login"
              to="/login"
              className="text-slate-400 hover:text-slate-200 text-sm transition-colors duration-200 px-3 py-1.5"
            >
              Iniciar sesión
            </Link>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Link
                data-testid="nav-btn-register"
                to="/register"
                className="flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-xl"
                style={{
                  background: "linear-gradient(135deg, #10B981, #06B6D4)",
                  color: "#020617",
                  boxShadow: "0 0 20px rgba(16,185,129,0.3), inset 0 1px 0 rgba(255,255,255,0.15)",
                }}
              >
                Empezar gratis <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </nav>

      {/* ════════════════════════════
          HERO
      ════════════════════════════ */}
      <section
        ref={heroRef}
        onMouseMove={handleHeroMouseMove}
        onMouseLeave={() => setCursorPos({ x: -2000, y: -2000 })}
        className="relative overflow-hidden"
        style={{ background: "#020617", minHeight: "92vh" }}
      >
        {/* Capa 1 – Ruido sutil */}
        <div className="pointer-events-none absolute inset-0 noise-overlay z-0" aria-hidden />

        {/* Capa 2 – Auroras animadas */}
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
          <div className="aurora-1 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 70% 60% at 12% 18%, rgba(16,185,129,0.20) 0%, transparent 100%)" }} />
          <div className="aurora-2 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 65% 55% at 88% 22%, rgba(6,182,212,0.16) 0%, transparent 100%)" }} />
          <div className="aurora-3 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 50% 45% at 55% 85%, rgba(139,92,246,0.13) 0%, transparent 100%)" }} />
          <div className="absolute inset-0"
            style={{ background: "radial-gradient(ellipse 38% 38% at 78% 65%, rgba(59,130,246,0.07) 0%, transparent 100%)" }} />
        </div>

        {/* Capa 3 – Grid tecnológico */}
        <div
          className="pointer-events-none absolute inset-0 z-0"
          aria-hidden
          style={{
            backgroundImage: "linear-gradient(rgba(148,163,184,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.5) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            opacity: 0.028,
            maskImage: "radial-gradient(ellipse 80% 70% at 50% 30%, black 20%, transparent 75%)",
          }}
        />

        {/* Capa 4 – Cursor glow (solo desktop) */}
        <div
          className="pointer-events-none absolute z-0 hidden lg:block"
          style={{
            width: 520,
            height: 520,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(16,185,129,0.055) 0%, transparent 70%)",
            transform: `translate(${cursorPos.x - 260}px, ${cursorPos.y - 260}px)`,
            transition: "transform 0.12s ease-out",
            willChange: "transform",
          }}
        />

        {/* Capa 5 – Contenido */}
        <div className="relative z-10 max-w-6xl mx-auto px-5 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-14 items-center">

            {/* ── Texto Hero ── */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } } }}
            >
              {/* Badge */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6"
                style={{
                  background: "rgba(16,185,129,0.09)",
                  border: "1px solid rgba(16,185,129,0.24)",
                  boxShadow: "0 0 20px rgba(16,185,129,0.08)",
                }}
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                </span>
                <span className="text-emerald-400 text-xs font-semibold tracking-wide">SECOP II · Actualización diaria automática</span>
              </motion.div>

              {/* H1 */}
              <motion.h1
                variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.65 } } }}
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] mb-5"
              >
                <span className="text-white">Gana más</span><br />
                <span style={{
                  background: "linear-gradient(90deg, #34D399, #22D3EE, #A78BFA)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}>
                  licitaciones públicas
                </span><br />
                <span className="text-white">con IA</span>
              </motion.h1>

              {/* Descripción */}
              <motion.p
                variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.55 } } }}
                className="text-base sm:text-lg leading-relaxed mb-8 max-w-lg"
                style={{ color: "#94A3B8" }}
              >
                Encuentra, evalúa y gana contratos del SECOP II con análisis de inteligencia artificial. Diseñado para empresas colombianas que quieren contratar más y mejor con el Estado.
              </motion.p>

              {/* CTAs */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="flex flex-col sm:flex-row gap-3 mb-8"
              >
                <motion.div whileHover={{ scale: 1.02, y: -1 }} whileTap={{ scale: 0.98 }}>
                  <Link
                    data-testid="hero-cta-primary"
                    to="/register"
                    className="flex items-center justify-center gap-2 font-extrabold px-8 py-4 rounded-xl text-sm sm:text-base group"
                    style={{
                      background: "linear-gradient(135deg, #10B981, #06B6D4)",
                      color: "#020617",
                      boxShadow: "0 0 40px rgba(16,185,129,0.45), inset 0 1px 0 rgba(255,255,255,0.15)",
                      transition: "box-shadow 0.2s ease",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 60px rgba(16,185,129,0.65), inset 0 1px 0 rgba(255,255,255,0.15)"; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 40px rgba(16,185,129,0.45), inset 0 1px 0 rgba(255,255,255,0.15)"; }}
                  >
                    Comenzar gratis — 7 días
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                  <a
                    data-testid="hero-cta-secondary"
                    href="#como-funciona"
                    className="flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-sm font-semibold"
                    style={{
                      border: "1px solid rgba(148,163,184,0.18)",
                      color: "#CBD5E1",
                      background: "rgba(15,23,42,0.5)",
                      backdropFilter: "blur(8px)",
                      transition: "border-color 0.2s, color 0.2s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(16,185,129,0.4)"; e.currentTarget.style.color = "#34D399"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(148,163,184,0.18)"; e.currentTarget.style.color = "#CBD5E1"; }}
                  >
                    Ver cómo funciona
                  </a>
                </motion.div>
              </motion.div>

              {/* Fine print */}
              <motion.p
                variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.4 } } }}
                className="text-xs flex items-center gap-1.5"
                style={{ color: "#475569" }}
              >
                <Lock className="w-3 h-3" /> Sin tarjeta de crédito · Cancela cuando quieras
              </motion.p>

              {/* Stats */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.1 } } }}
                className="grid grid-cols-3 gap-4 mt-10 pt-8"
                style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
              >
                {[
                  { val: "+$1.2B", label: "COP en oportunidades" },
                  { val: "+15K",  label: "procesos analizados" },
                  { val: "98.4%", label: "precisión IA" },
                ].map(({ val, label }) => (
                  <div key={label}>
                    <div
                      className="text-xl sm:text-2xl font-extrabold font-mono"
                      style={{ background: "linear-gradient(90deg, #34D399, #22D3EE)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                    >{val}</div>
                    <div className="text-xs mt-0.5" style={{ color: "#64748B" }}>{label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* ── Widget Radar ── */}
            <motion.div
              initial={{ opacity: 0, x: 32, filter: "blur(10px)" }}
              animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.85, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.3 }}
              data-testid="radar-live-widget"
              className="relative rounded-2xl overflow-hidden"
              style={{
                background: "rgba(6,11,24,0.92)",
                backdropFilter: "blur(32px)",
                border: "1px solid rgba(16,185,129,0.22)",
                boxShadow: "0 0 0 1px rgba(16,185,129,0.06), 0 0 80px rgba(16,185,129,0.10), 0 0 40px rgba(6,182,212,0.05), 0 32px 80px rgba(0,0,0,0.7)",
              }}
            >
              {/* Halo interior sutil */}
              <div
                className="pointer-events-none absolute -inset-px rounded-2xl"
                style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.07) 0%, rgba(6,182,212,0.03) 50%, rgba(139,92,246,0.05) 100%)" }}
              />

              {/* Header widget */}
              <div
                className="flex items-center justify-between px-5 py-3.5 border-b"
                style={{ background: "rgba(3,7,16,0.96)", borderColor: "rgba(255,255,255,0.055)" }}
              >
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-emerald-400" />
                  <span className="text-slate-200 text-sm font-semibold">Radar SECOP II</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                  </span>
                  <span className="text-emerald-400 text-xs font-bold tracking-wide">EN VIVO</span>
                </div>
              </div>

              {/* Filtros mock */}
              <div className="px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
                <div
                  className="flex-1 flex items-center gap-2 rounded-lg px-3 py-1.5"
                  style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  <Search className="w-3 h-3 text-slate-600" />
                  <span className="text-slate-600 text-xs">Infraestructura, tecnología, servicios...</span>
                </div>
                <div
                  className="flex items-center gap-1.5 rounded-lg px-3 py-1.5"
                  style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  <Filter className="w-3 h-3 text-slate-400" />
                  <span className="text-slate-400 text-xs">Filtros</span>
                </div>
              </div>

              {/* Filas de procesos */}
              <div>
                {LIVE_PROCESSES.map((p, i) => (
                  <div
                    key={p.id}
                    className="px-5 py-3.5 transition-all duration-500"
                    style={{
                      background: i === ticker ? "rgba(16,185,129,0.055)" : "transparent",
                      borderBottom: i < LIVE_PROCESSES.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                      borderLeft: i === ticker ? "2px solid rgba(16,185,129,0.5)" : "2px solid transparent",
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <PriorityBadge priority={p.priority} />
                          <span
                            className="text-[10px] text-slate-500 rounded px-1.5 py-0.5"
                            style={{ background: "rgba(255,255,255,0.05)" }}
                          >{p.tag}</span>
                        </div>
                        <p className="text-slate-200 text-xs font-medium truncate">{p.entity}</p>
                        <p className="text-[11px] mt-0.5" style={{ color: "#475569" }}>{p.dept} · {p.value}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <span
                          className="font-mono font-bold text-sm"
                          style={{
                            color: p.score >= 85 ? "#34D399" : p.score >= 70 ? "#FCD34D" : "#94A3B8",
                            textShadow: p.score >= 85 ? "0 0 12px rgba(52,211,153,0.7)" : "none",
                          }}
                        >
                          {p.score}<span className="text-xs" style={{ color: "#334155" }}>/100</span>
                        </span>
                        <p className="text-[10px] mt-0.5" style={{ color: "#1E293B" }}>Score IA</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer widget */}
              <div
                className="px-5 py-3 border-t flex items-center justify-between"
                style={{ background: "rgba(3,7,16,0.96)", borderColor: "rgba(255,255,255,0.055)" }}
              >
                <span className="text-xs" style={{ color: "#334155" }}>15.284 procesos activos</span>
                <span className="text-emerald-400 text-xs font-medium">Ver todos →</span>
              </div>

              {/* Blur-lock overlay + CTA */}
              <div
                className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
                style={{ background: "linear-gradient(to top, rgba(3,7,16,0.98), transparent)" }}
              />
              <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                  <Link
                    to="/register"
                    className="flex items-center gap-2 font-bold text-xs px-5 py-2 rounded-full"
                    style={{
                      background: "linear-gradient(135deg, #10B981, #06B6D4)",
                      color: "#020617",
                      boxShadow: "0 0 22px rgba(16,185,129,0.45)",
                      transition: "box-shadow 0.2s ease",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 35px rgba(16,185,129,0.65)"; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 22px rgba(16,185,129,0.45)"; }}
                  >
                    <Lock className="w-3 h-3" /> Desbloquear acceso completo
                  </Link>
                </motion.div>
              </div>
            </motion.div>

          </div>
        </div>

        {/* Transición hero → trust bar */}
        <div
          className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none z-20"
          style={{ background: "linear-gradient(to bottom, transparent, rgba(248,250,252,0.08))" }}
        />
      </section>

      {/* ════════════════════════════
          TRUST BAR
      ════════════════════════════ */}
      <section
        className="relative z-10 py-5"
        style={{ background: "#F8FAFC", borderTop: "1px solid #E2E8F0", borderBottom: "1px solid #E2E8F0" }}
      >
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={VP}
          variants={SV}
          className="max-w-5xl mx-auto px-5"
        >
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {[
              { icon: Shield,    label: "Datos oficiales SECOP II" },
              { icon: TrendingUp, label: "Colombia Compra Eficiente" },
              { icon: Award,    label: "Gemini 3 Flash IA" },
              { icon: Users,    label: "Multi-empresa" },
              { icon: Clock,    label: "Actualización diaria" },
            ].map(({ icon: Icon, label }) => (
              <motion.div
                key={label}
                variants={IV}
                className="flex items-center gap-2 font-semibold text-xs sm:text-sm"
                style={{ color: "#475569" }}
              >
                <Icon className="w-4 h-4" style={{ color: "#059669" }} />
                <span>{label}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ════════════════════════════
          CÓMO FUNCIONA
      ════════════════════════════ */}
      <section
        id="como-funciona"
        className="relative z-10 bg-white py-20 lg:py-28"
        style={{ borderBottom: "1px solid #F1F5F9" }}
      >
        <div className="max-w-5xl mx-auto px-5">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
            className="text-center mb-16"
          >
            <motion.span variants={IV} className="text-xs font-bold tracking-widest uppercase mb-3 block" style={{ color: "#059669" }}>
              Proceso simple
            </motion.span>
            <motion.h2 variants={IV} className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3" style={{ color: "#0F172A" }}>
              Empieza a ganar en 3 pasos
            </motion.h2>
            <motion.p variants={IV} className="max-w-lg mx-auto text-sm sm:text-base" style={{ color: "#64748B" }}>
              Sin configuraciones complicadas. En menos de 5 minutos tienes tu radar activo.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
            className="grid md:grid-cols-3 gap-6 relative"
          >
            {/* Línea conectora con gradiente */}
            <div
              className="hidden md:block absolute pointer-events-none"
              style={{ top: "1.45rem", left: "calc(33.33% + 14px)", right: "calc(33.33% + 14px)", height: "1px", overflow: "hidden" }}
            >
              <div style={{ height: "1px", background: "linear-gradient(90deg, #10B981, #06B6D4, #8B5CF6)", opacity: 0.45 }} />
              <div
                className="shimmer-line absolute inset-0"
                style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.75) 50%, transparent 100%)", height: "1px" }}
              />
            </div>

            {STEPS.map(({ num, title, desc }) => (
              <motion.div
                key={num}
                variants={IV}
                className="group bg-white rounded-2xl p-7"
                style={{ border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
                whileHover={{ y: -5, boxShadow: "0 18px 44px rgba(16,185,129,0.10), 0 4px 14px rgba(0,0,0,0.06)", borderColor: "rgba(16,185,129,0.35)" }}
                transition={{ duration: 0.25 }}
              >
                <motion.div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 font-mono font-black text-lg"
                  style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", color: "#059669", boxShadow: "0 2px 8px rgba(16,185,129,0.12)" }}
                  whileHover={{ background: "#10B981", borderColor: "#10B981", color: "white", boxShadow: "0 0 18px rgba(16,185,129,0.45)" }}
                  transition={{ duration: 0.2 }}
                >
                  {num}
                </motion.div>
                <h3 className="font-bold text-lg mb-2" style={{ color: "#0F172A" }}>{title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#64748B" }}>{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════
          CARACTERÍSTICAS
      ════════════════════════════ */}
      <section
        id="caracteristicas"
        className="relative z-10 py-20 lg:py-28"
        style={{ background: "#F1F5F9", borderBottom: "1px solid #E2E8F0" }}
      >
        <div className="max-w-5xl mx-auto px-5">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
            className="text-center mb-14"
          >
            <motion.span variants={IV} className="text-xs font-bold tracking-widest uppercase mb-3 block" style={{ color: "#059669" }}>
              Funcionalidades
            </motion.span>
            <motion.h2 variants={IV} className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3" style={{ color: "#0F172A" }}>
              Todo lo que necesitas
            </motion.h2>
            <motion.p variants={IV} className="max-w-lg mx-auto text-sm sm:text-base" style={{ color: "#64748B" }}>
              para contratar con el Estado colombiano de manera inteligente.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
            className="grid sm:grid-cols-2 gap-5"
          >
            {FEATURES.map(({ icon: Icon, badge, title, desc, stat }) => (
              <motion.div
                key={title}
                variants={IV}
                className="group bg-white rounded-2xl p-7"
                style={{ border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", cursor: "default" }}
                whileHover={{ y: -5, boxShadow: "0 22px 52px rgba(16,185,129,0.09), 0 4px 16px rgba(0,0,0,0.06)", borderColor: "rgba(16,185,129,0.35)" }}
                transition={{ duration: 0.25 }}
              >
                <div className="flex items-start justify-between mb-5">
                  <motion.div
                    className="w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{ background: "#ECFDF5", border: "1px solid #A7F3D0" }}
                    whileHover={{ background: "#10B981", borderColor: "#10B981", boxShadow: "0 0 18px rgba(16,185,129,0.4)" }}
                    transition={{ duration: 0.2 }}
                  >
                    <Icon className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors duration-200" />
                  </motion.div>
                  <span
                    className="text-[11px] font-bold rounded-full px-3 py-1"
                    style={{ color: "#047857", background: "#ECFDF5", border: "1px solid #A7F3D0" }}
                  >{badge}</span>
                </div>
                <h3 className="font-bold text-base mb-2" style={{ color: "#0F172A" }}>{title}</h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: "#64748B" }}>{desc}</p>
                <div className="pt-3" style={{ borderTop: "1px solid #F1F5F9" }}>
                  <span className="text-xs font-bold font-mono" style={{ color: "#059669" }}>{stat}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════
          PRECIOS
      ════════════════════════════ */}
      <section
        id="precios"
        className="relative z-10 bg-white py-20 lg:py-28"
        style={{ borderBottom: "1px solid #F1F5F9" }}
      >
        <div className="max-w-4xl mx-auto px-5">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
            className="text-center mb-14"
          >
            <motion.span variants={IV} className="text-xs font-bold tracking-widest uppercase mb-3 block" style={{ color: "#059669" }}>
              Planes
            </motion.span>
            <motion.h2 variants={IV} className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3" style={{ color: "#0F172A" }}>
              Precios simples y claros
            </motion.h2>
            <motion.p variants={IV} className="max-w-lg mx-auto text-sm sm:text-base" style={{ color: "#64748B" }}>
              7 días gratis en cualquier plan. Sin tarjeta de crédito.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.14 } } }}
            className="grid sm:grid-cols-2 gap-6 items-start"
          >
            {/* Plan Básico */}
            <motion.div
              variants={IV}
              data-testid="pricing-card-basic"
              className="bg-white rounded-2xl p-8"
              style={{ border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
              whileHover={{ y: -2, boxShadow: "0 10px 32px rgba(0,0,0,0.08)" }}
              transition={{ duration: 0.22 }}
            >
              <p className="text-sm font-medium mb-1" style={{ color: "#64748B" }}>Plan Básico</p>
              <p className="text-xs mb-5" style={{ color: "#94A3B8" }}>Ideal para independientes y pequeñas empresas</p>
              <div className="flex items-end gap-1 mb-7">
                <span className="font-black font-mono text-4xl" style={{ color: "#0F172A" }}>$17.000</span>
                <span className="text-sm mb-1.5" style={{ color: "#94A3B8" }}>COP/mes</span>
              </div>
              <ul className="space-y-3 mb-7">
                {["Radar SECOP II completo", "Análisis IA (50 procesos/día)", "Score automático 0-100", "Alertas personalizadas por email", "1 usuario activo"].map(f => (
                  <li key={f} className="flex items-center gap-2.5 text-sm" style={{ color: "#475569" }}>
                    <CheckCircle className="w-4 h-4 shrink-0" style={{ color: "#10B981" }} />{f}
                  </li>
                ))}
              </ul>
              <Link
                data-testid="pricing-btn-basic"
                to="/register"
                className="block w-full text-center rounded-xl py-3 text-sm font-semibold"
                style={{ border: "1px solid #CBD5E1", color: "#475569", transition: "border-color 0.2s, color 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#10B981"; e.currentTarget.style.color = "#059669"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#CBD5E1"; e.currentTarget.style.color = "#475569"; }}
              >
                Empezar 7 días gratis
              </Link>
            </motion.div>

            {/* Plan Premium */}
            <motion.div
              variants={IV}
              data-testid="pricing-card-premium"
              className="relative rounded-2xl p-8"
              style={{
                background: "linear-gradient(160deg, rgba(236,253,245,0.65) 0%, #ffffff 38%)",
                border: "2px solid #10B981",
                boxShadow: "0 0 0 4px rgba(16,185,129,0.06), 0 24px 64px rgba(16,185,129,0.13), 0 4px 20px rgba(0,0,0,0.05)",
                transform: "scale(1.02)",
              }}
              whileHover={{ y: -2, boxShadow: "0 0 0 4px rgba(16,185,129,0.08), 0 30px 80px rgba(16,185,129,0.18), 0 4px 20px rgba(0,0,0,0.06)" }}
              transition={{ duration: 0.22 }}
            >
              <div
                className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-white text-[11px] font-black tracking-wider px-4 py-1 rounded-full shadow-md"
                style={{ background: "linear-gradient(135deg, #059669, #0891B2)" }}
              >
                MÁS POPULAR
              </div>
              <p className="text-sm font-bold mb-1" style={{ color: "#059669" }}>Plan Premium</p>
              <p className="text-xs mb-5" style={{ color: "#94A3B8" }}>Para empresas que quieren maximizar sus licitaciones</p>
              <div className="flex items-end gap-1 mb-7">
                <span className="font-black font-mono text-4xl" style={{ color: "#0F172A" }}>$29.700</span>
                <span className="text-sm mb-1.5" style={{ color: "#94A3B8" }}>COP/mes</span>
              </div>
              <ul className="space-y-3 mb-7">
                {["Todo lo del Plan Básico", "Análisis IA ilimitado", "Alertas en tiempo real", "Perfil de empresa avanzado", "Múltiples usuarios", "Soporte prioritario"].map(f => (
                  <li key={f} className="flex items-center gap-2.5 text-sm" style={{ color: "#475569" }}>
                    <CheckCircle className="w-4 h-4 shrink-0" style={{ color: "#10B981" }} />{f}
                  </li>
                ))}
              </ul>
              <Link
                data-testid="pricing-btn-premium"
                to="/register"
                className="block w-full text-center font-extrabold rounded-xl py-3 text-sm"
                style={{
                  background: "linear-gradient(135deg, #10B981, #06B6D4)",
                  color: "#020617",
                  boxShadow: "0 0 24px rgba(16,185,129,0.4)",
                  transition: "box-shadow 0.2s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 38px rgba(16,185,129,0.65)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 24px rgba(16,185,129,0.4)"; }}
              >
                Empezar 7 días gratis
              </Link>
            </motion.div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={VP}
            className="text-center text-xs mt-6 flex items-center justify-center gap-1.5"
            style={{ color: "#94A3B8" }}
          >
            <Lock className="w-3 h-3" /> Pago seguro · Sin compromiso · Cancela cuando quieras
          </motion.p>
        </div>
      </section>

      {/* ════════════════════════════
          FAQ
      ════════════════════════════ */}
      <section
        className="relative z-10 py-20 lg:py-28"
        style={{ background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}
      >
        <div className="max-w-2xl mx-auto px-5">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
            className="text-center mb-10"
          >
            <motion.h2 variants={IV} className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2" style={{ color: "#0F172A" }}>
              Preguntas frecuentes
            </motion.h2>
            <motion.p variants={IV} className="text-sm" style={{ color: "#64748B" }}>
              Todo lo que necesitas saber antes de empezar.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
          >
            {FAQS.map(faq => (
              <motion.div key={faq.q} variants={IV}>
                <FaqItem {...faq} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════
          CTA FINAL
      ════════════════════════════ */}
      <section
        className="relative z-10 overflow-hidden"
        style={{ background: "#020617" }}
      >
        {/* Auroras CTA */}
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          <div className="aurora-1 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 60% 50% at 18% 50%, rgba(16,185,129,0.17) 0%, transparent 100%)" }} />
          <div className="aurora-2 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 55% 45% at 82% 50%, rgba(6,182,212,0.13) 0%, transparent 100%)" }} />
          <div className="aurora-3 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 42% 42% at 52% 82%, rgba(139,92,246,0.11) 0%, transparent 100%)" }} />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-5 py-20 lg:py-28">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
            className="relative rounded-3xl p-10 sm:p-16 text-center overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(16,185,129,0.07) 0%, rgba(6,11,24,0.97) 50%, rgba(139,92,246,0.06) 100%)",
              border: "1px solid rgba(16,185,129,0.18)",
              boxShadow: "0 0 0 1px rgba(16,185,129,0.06), 0 0 100px rgba(16,185,129,0.08)",
            }}
          >
            {/* Glow interno superior */}
            <div
              className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-96 h-44"
              style={{ background: "radial-gradient(ellipse at center, rgba(16,185,129,0.11) 0%, transparent 70%)", filter: "blur(24px)" }}
            />

            <motion.div variants={IV} className="flex items-center justify-center gap-1 mb-5">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-emerald-400 text-emerald-400" />)}
            </motion.div>

            <motion.h2
              variants={IV}
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-4"
            >
              No dejes pasar más<br />
              <span style={{
                background: "linear-gradient(90deg, #34D399, #22D3EE, #A78BFA)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>
                contratos públicos
              </span>
            </motion.h2>

            <motion.p variants={IV} className="mb-8 max-w-md mx-auto text-sm sm:text-base" style={{ color: "#64748B" }}>
              Regístrate en 30 segundos y empieza a descubrir licitaciones del SECOP II que se ajustan exactamente a tu empresa.
            </motion.p>

            <motion.div variants={IV}>
              <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }} className="inline-block">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2.5 font-extrabold px-10 py-4 rounded-xl text-base group"
                  style={{
                    background: "linear-gradient(135deg, #10B981, #06B6D4)",
                    color: "#020617",
                    boxShadow: "0 0 50px rgba(16,185,129,0.50)",
                    transition: "box-shadow 0.2s ease",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 70px rgba(16,185,129,0.70)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 50px rgba(16,185,129,0.50)"; }}
                >
                  Comenzar gratis — 7 días
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>

            <motion.p variants={IV} className="text-xs mt-4" style={{ color: "#334155" }}>
              Sin tarjeta de crédito · Cancela cuando quieras
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════
          FOOTER
      ════════════════════════════ */}
      <footer
        className="relative z-10 px-5 py-12"
        style={{ background: "#020617", borderTop: "1px solid rgba(255,255,255,0.05)" }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <div data-testid="footer-brand-text" className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)" }}
              >
                <Target className="w-3.5 h-3.5 text-emerald-400" />
              </div>
              <span className="font-bold" style={{ color: "#94A3B8" }}>RadarLit</span>
            </div>
            <div className="flex items-center gap-5 text-xs" style={{ color: "#475569" }}>
              <Link to="/precios" className="hover:text-slate-300 transition-colors duration-200">Precios</Link>
              <Link to="/login" className="hover:text-slate-300 transition-colors duration-200">Iniciar sesión</Link>
              <Link to="/register" className="hover:text-slate-300 transition-colors duration-200">Registro</Link>
            </div>
          </div>
          <div
            className="pt-5 flex flex-col sm:flex-row items-center justify-between gap-2"
            style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}
          >
            <p className="text-xs" style={{ color: "#334155" }}>© 2025 RadarLit · Desarrollado por Innova App Solutions · radarlit.com</p>
            <div className="flex items-center gap-1.5 text-xs" style={{ color: "#1E293B" }}>
              <Shield className="w-3 h-3" style={{ color: "rgba(16,185,129,0.4)" }} />
              <span>Datos oficiales SECOP II</span>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
